import React, { useEffect } from 'react'
import { Navigate, Route, RouteProps } from 'react-router-dom'

import { Loader } from '@percent/cause-dashboard/common/components'

import { RoutePath } from '@percent/cause-dashboard/app/routes/Routes'
import { isProAppPath } from '@percent/cause-dashboard/constants/goodstackPro'
import { externalRedirect } from '@percent/cause-dashboard/common/utility/externalRedirect/externalRedirect'
import { PostHogFeatureFlag, useAuthState, usePostHogFeatureFlag } from '@percent/cause-dashboard/common/hooks'
import { AuthState, selectAuthState } from '@percent/cause-dashboard/context/auth'
import { useLocation } from 'react-router-dom'

export enum UserState {
  LoggedOut = 'LoggedOut',
  LoggedInUnverified = 'LoggedInUnverified',
  LoggedIn = 'LoggedIn',
  LoggedInClaimMade = 'LoggedInClaimMade',
  Any = 'Any'
}
const ExternalRedirect = ({ to }: { to: string }) => {
  useEffect(() => {
    externalRedirect(to)
  }, [to])

  return <Loader />
}

const isLoggedInUnverified = ({ loggedIn, userVerified }: AuthState): boolean => loggedIn && !userVerified

const isLoggedInVerified = ({ loggedIn, userVerified, claimMade }: AuthState): boolean =>
  loggedIn && userVerified && !claimMade

const isLoggedInClaimMade = ({ loggedIn, userVerified, claimMade }: AuthState): boolean =>
  loggedIn && userVerified && claimMade

export const mapAuthStateToUserState = (authState: AuthState): UserState => {
  if (isLoggedInUnverified(authState)) return UserState.LoggedInUnverified
  if (isLoggedInVerified(authState)) return UserState.LoggedIn
  if (isLoggedInClaimMade(authState)) return UserState.LoggedInClaimMade
  return UserState.LoggedOut
}

export const getRedirectionRouteForState = ({
  authState,
  urlAfterLogin
}: {
  authState: AuthState
  urlAfterLogin: string
}) => {
  const nextParam = encodeURIComponent(`${window.location.pathname}${window.location.search}`)

  if (authState.sessionExpired) {
    return `${RoutePath.EXPIRED_SESSION}?next=${nextParam}`
  }

  if (isLoggedInUnverified(authState)) {
    return RoutePath.EMAIL_VERIFICATION
  }

  if (isLoggedInClaimMade(authState)) {
    return urlAfterLogin?.startsWith('/') ? urlAfterLogin : RoutePath.HOME
  }

  // When organisation is unclaimed, account stays in system and user can login.
  if (isLoggedInVerified(authState)) {
    return RoutePath.CLAIM
  }

  return `${RoutePath.SIGNIN}?next=${nextParam}`
}

const canAccessRoute = (authState: AuthState, allowedStates: UserState[], disallowedStatuses: UserState[]) => {
  if (allowedStates.includes(UserState.Any)) return true

  const userState = mapAuthStateToUserState(authState)

  if (disallowedStatuses.some(disallowedStatuses => disallowedStatuses === userState)) {
    return false
  }
  return allowedStates.some(allowedState => allowedState === userState)
}

export type AuthRouteProps = RouteProps & {
  component?: React.ComponentType<any>
  allow: UserState[]
  disallow?: UserState[]
  feature?: PostHogFeatureFlag
}

export const AuthRouteElement = ({
  allow,
  disallow,
  feature,
  component: Component,
  children: Children
}: {
  allow: UserState[]
  disallow?: UserState[]
  feature?: PostHogFeatureFlag
  component?: React.ComponentType<any>
  children?: React.ReactNode
}) => {
  const {
    authState: appState,
    authState: { user, isAuthorised }
  } = useAuthState()
  const authState = selectAuthState(appState)
  const featureEnabled = usePostHogFeatureFlag(feature)
  const location = useLocation()
  const nextUrl = new URLSearchParams(location.search).get('next')
  const redirectRoute = nextUrl ? decodeURIComponent(nextUrl) : RoutePath.HOME

  if (isAuthorised && !user) {
    return <Loader />
  }

  const allowFeature = !feature || featureEnabled

  if (!allowFeature || !canAccessRoute(authState, allow, disallow || [])) {
    const redirectionRoute = getRedirectionRouteForState({ authState, urlAfterLogin: redirectRoute })

    if (isProAppPath(redirectionRoute)) {
      return <ExternalRedirect to={redirectionRoute} />
    }

    return <Navigate to={redirectionRoute} replace />
  }

  if (Component) {
    return <Component />
  }

  if (Children) {
    return <>{Children}</>
  }

  return null
}

export const AuthRoute = ({
  allow,
  disallow,
  feature,
  component: Component,
  children: Children,
  ...rest
}: AuthRouteProps) => {
  // React Router v6 requires Route components to be direct children of Routes
  // The Route element type must be Route, not a wrapper component
  // We return a Route with our auth logic in the element prop
  return (
    <Route
      {...rest}
      element={
        <AuthRouteElement allow={allow} disallow={disallow} feature={feature} component={Component}>
          {Children}
        </AuthRouteElement>
      }
    />
  )
}
